interface Point {
  x: number;
  y: number;
}

/**
 * Compute the perspective transformation matrix from source to destination points
 * Uses the Direct Linear Transform (DLT) algorithm
 */
function computeHomography(src: Point[], dst: Point[]): number[] | null {
  if (src.length !== 4 || dst.length !== 4) return null;

  // Build the matrix A for Ax = 0
  const A: number[][] = [];
  
  for (let i = 0; i < 4; i++) {
    const { x: sx, y: sy } = src[i];
    const { x: dx, y: dy } = dst[i];
    
    A.push([sx, sy, 1, 0, 0, 0, -dx * sx, -dx * sy, -dx]);
    A.push([0, 0, 0, sx, sy, 1, -dy * sx, -dy * sy, -dy]);
  }

  // Solve using simplified SVD-like approach for 3x3 homography
  // We need to solve Ah = 0, where h is the 9-element homography vector
  
  // Use Gaussian elimination with back substitution
  const h = solveHomography(A);
  return h;
}

/**
 * Solve the homography system using Gaussian elimination
 */
function solveHomography(A: number[][]): number[] | null {
  const n = 8;
  const m = 9;
  
  // Augmented matrix [A | 0], we'll solve for h8 = 1
  const M = A.map(row => [...row]);
  
  // Forward elimination
  for (let col = 0; col < n; col++) {
    // Find pivot
    let maxRow = col;
    for (let row = col + 1; row < n; row++) {
      if (Math.abs(M[row][col]) > Math.abs(M[maxRow][col])) {
        maxRow = row;
      }
    }
    
    // Swap rows
    [M[col], M[maxRow]] = [M[maxRow], M[col]];
    
    if (Math.abs(M[col][col]) < 1e-10) {
      continue;
    }
    
    // Eliminate column
    for (let row = col + 1; row < n; row++) {
      const factor = M[row][col] / M[col][col];
      for (let j = col; j < m; j++) {
        M[row][j] -= factor * M[col][j];
      }
    }
  }
  
  // Back substitution with h8 = 1
  const h = new Array(9).fill(0);
  h[8] = 1;
  
  for (let i = n - 1; i >= 0; i--) {
    let sum = -M[i][8] * h[8]; // Move h8 term to right side
    for (let j = i + 1; j < n; j++) {
      sum -= M[i][j] * h[j];
    }
    if (Math.abs(M[i][i]) > 1e-10) {
      h[i] = sum / M[i][i];
    }
  }
  
  return h;
}

/**
 * Apply perspective transform to get source coordinates from destination coordinates
 */
function perspectiveTransformInverse(dx: number, dy: number, H: number[]): Point {
  // Inverse transform: given destination point, find source point
  // We need the inverse of H, but for simplicity we'll compute it differently
  const w = H[6] * dx + H[7] * dy + H[8];
  if (Math.abs(w) < 1e-10) {
    return { x: 0, y: 0 };
  }
  
  const sx = (H[0] * dx + H[1] * dy + H[2]) / w;
  const sy = (H[3] * dx + H[4] * dy + H[5]) / w;
  
  return { x: sx, y: sy };
}

/**
 * Invert a 3x3 homography matrix
 */
function invertHomography(H: number[]): number[] | null {
  const [a, b, c, d, e, f, g, h, i] = H;
  
  const det = a * (e * i - f * h) - b * (d * i - f * g) + c * (d * h - e * g);
  
  if (Math.abs(det) < 1e-10) return null;
  
  const invDet = 1 / det;
  
  return [
    (e * i - f * h) * invDet,
    (c * h - b * i) * invDet,
    (b * f - c * e) * invDet,
    (f * g - d * i) * invDet,
    (a * i - c * g) * invDet,
    (c * d - a * f) * invDet,
    (d * h - e * g) * invDet,
    (b * g - a * h) * invDet,
    (a * e - b * d) * invDet,
  ];
}

/**
 * Bilinear interpolation for smooth sampling
 */
function bilinearSample(
  data: Uint8ClampedArray,
  width: number,
  height: number,
  x: number,
  y: number
): [number, number, number, number] {
  const x0 = Math.floor(x);
  const y0 = Math.floor(y);
  const x1 = Math.min(x0 + 1, width - 1);
  const y1 = Math.min(y0 + 1, height - 1);
  
  const fx = x - x0;
  const fy = y - y0;
  
  const getPixel = (px: number, py: number): [number, number, number, number] => {
    if (px < 0 || px >= width || py < 0 || py >= height) {
      return [255, 255, 255, 255]; // White for out of bounds
    }
    const idx = (py * width + px) * 4;
    return [data[idx], data[idx + 1], data[idx + 2], data[idx + 3]];
  };
  
  const p00 = getPixel(x0, y0);
  const p10 = getPixel(x1, y0);
  const p01 = getPixel(x0, y1);
  const p11 = getPixel(x1, y1);
  
  const result: [number, number, number, number] = [0, 0, 0, 0];
  for (let c = 0; c < 4; c++) {
    const top = p00[c] * (1 - fx) + p10[c] * fx;
    const bottom = p01[c] * (1 - fx) + p11[c] * fx;
    result[c] = Math.round(top * (1 - fy) + bottom * fy);
  }
  
  return result;
}

/**
 * Order corners in consistent order: top-left, top-right, bottom-right, bottom-left
 */
function orderCorners(corners: Point[]): Point[] {
  if (corners.length !== 4) return corners;
  
  // Find center
  const center = {
    x: corners.reduce((sum, p) => sum + p.x, 0) / 4,
    y: corners.reduce((sum, p) => sum + p.y, 0) / 4,
  };
  
  // Sort by angle from center
  const sorted = [...corners].sort((a, b) => {
    const angleA = Math.atan2(a.y - center.y, a.x - center.x);
    const angleB = Math.atan2(b.y - center.y, b.x - center.x);
    return angleA - angleB;
  });
  
  // Find top-left (smallest x + y)
  let topLeftIdx = 0;
  let minSum = Infinity;
  for (let i = 0; i < 4; i++) {
    const sum = sorted[i].x + sorted[i].y;
    if (sum < minSum) {
      minSum = sum;
      topLeftIdx = i;
    }
  }
  
  // Rotate array so top-left is first
  const ordered: Point[] = [];
  for (let i = 0; i < 4; i++) {
    ordered.push(sorted[(topLeftIdx + i) % 4]);
  }
  
  return ordered;
}

/**
 * Apply perspective correction to an image given 4 corner points
 */
export function applyPerspectiveCorrection(
  img: HTMLImageElement,
  corners: Point[]
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    if (corners.length !== 4) {
      reject(new Error("Exactly 4 corners required"));
      return;
    }
    
    // Order corners consistently
    const srcCorners = orderCorners(corners);
    
    // Calculate output dimensions based on the quadrilateral
    const width1 = Math.hypot(srcCorners[1].x - srcCorners[0].x, srcCorners[1].y - srcCorners[0].y);
    const width2 = Math.hypot(srcCorners[2].x - srcCorners[3].x, srcCorners[2].y - srcCorners[3].y);
    const height1 = Math.hypot(srcCorners[3].x - srcCorners[0].x, srcCorners[3].y - srcCorners[0].y);
    const height2 = Math.hypot(srcCorners[2].x - srcCorners[1].x, srcCorners[2].y - srcCorners[1].y);
    
    const outputWidth = Math.round(Math.max(width1, width2));
    const outputHeight = Math.round(Math.max(height1, height2));
    
    // Limit output size for performance
    const maxDim = 3000;
    const scale = Math.min(1, maxDim / Math.max(outputWidth, outputHeight));
    const finalWidth = Math.round(outputWidth * scale);
    const finalHeight = Math.round(outputHeight * scale);
    
    // Destination corners (rectangle)
    const dstCorners: Point[] = [
      { x: 0, y: 0 },
      { x: finalWidth, y: 0 },
      { x: finalWidth, y: finalHeight },
      { x: 0, y: finalHeight },
    ];
    
    // Compute homography from destination to source (for inverse mapping)
    const H = computeHomography(dstCorners, srcCorners);
    if (!H) {
      reject(new Error("Failed to compute homography"));
      return;
    }
    
    // Create source canvas
    const srcCanvas = document.createElement("canvas");
    srcCanvas.width = img.width;
    srcCanvas.height = img.height;
    const srcCtx = srcCanvas.getContext("2d");
    if (!srcCtx) {
      reject(new Error("Failed to get source context"));
      return;
    }
    srcCtx.drawImage(img, 0, 0);
    const srcData = srcCtx.getImageData(0, 0, img.width, img.height);
    
    // Create output canvas
    const outCanvas = document.createElement("canvas");
    outCanvas.width = finalWidth;
    outCanvas.height = finalHeight;
    const outCtx = outCanvas.getContext("2d");
    if (!outCtx) {
      reject(new Error("Failed to get output context"));
      return;
    }
    
    const outImageData = outCtx.createImageData(finalWidth, finalHeight);
    const outData = outImageData.data;
    
    // Apply perspective transformation using inverse mapping
    for (let dy = 0; dy < finalHeight; dy++) {
      for (let dx = 0; dx < finalWidth; dx++) {
        // Map destination pixel to source pixel
        const src = perspectiveTransformInverse(dx, dy, H);
        
        // Sample source with bilinear interpolation
        const [r, g, b, a] = bilinearSample(
          srcData.data,
          img.width,
          img.height,
          src.x,
          src.y
        );
        
        const idx = (dy * finalWidth + dx) * 4;
        outData[idx] = r;
        outData[idx + 1] = g;
        outData[idx + 2] = b;
        outData[idx + 3] = a;
      }
    }
    
    outCtx.putImageData(outImageData, 0, 0);
    
    outCanvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error("Failed to create blob"));
        }
      },
      "image/jpeg",
      0.92
    );
  });
}
